// Frontend-only Razorpay helper
// Usage: import { openRazorpayCheckout } from '../utilities/razorpayClient';

type RazorpayOptions = {
  amount: number; // in rupees (will convert to paise)
  currency?: string;
  name?: string;
  description?: string;
  image?: string;
  order_id?: string | null;
  key?: string; // optional: server-provided Razorpay key to override env
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
};

type RazorpayInstance = {
  open: () => void;
  on?: (event: "payment.failed", callback: (response: Record<string, unknown>) => void) => void;
};

type RazorpayConstructor = new (opts: Record<string, unknown>) => RazorpayInstance;

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_ORDER_ID_PATTERN = /^order_[A-Za-z0-9]+$/;

const loadScript = (src: string) => {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load script: " + src));
    document.body.appendChild(script);
  });
};

const assertValidOrderId = (orderId?: string | null) => {
  if (!orderId) return;

  if (!RAZORPAY_ORDER_ID_PATTERN.test(orderId)) {
    throw new Error("Invalid Razorpay order id.");
  }
};

export const openRazorpayCheckout = async (opts: RazorpayOptions) => {
  // Support an explicit key passed from server response; otherwise fall back to env var names
  // const envKey =
  //   process.env.REACT_APP_RAZORPAY_KEY_ID ||
  //   process.env.RAZER_PAY_ID ||
  //   process.env.REACT_APP_RAZER_PAY_ID ||
  //   process.env.REACT_APP_RAZOR_PAY_ID;
  const key = (opts && (opts as any).key);
  if (!key) throw new Error("Razorpay key is not set (pass `key` in options or set REACT_APP_RAZORPAY_KEY_ID)");

  assertValidOrderId(opts.order_id);

  await loadScript(RAZORPAY_SCRIPT_URL);
  return new Promise<Record<string, unknown>>((resolve, reject) => {
    let settled = false;

    const resolveOnce = (response: Record<string, unknown>) => {
      if (settled) return;
      settled = true;
      resolve(response);
    };

    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    try {
      const options: Record<string, unknown> = {
        key,
        amount: Math.round(opts.amount * 100), // paise
        currency: opts.currency || "INR",
        name: opts.name || "Chic Stylista",
        description: opts.description || "Order Payment",
        image: opts.image || undefined,
        order_id: opts.order_id || undefined,
        method: {
          card: true,
          upi: true,
          netbanking: true,
          wallet: true,
          emi: false,
          paylater: false,
        },
        allowed_payment_methods: ["card", "upi", "netbanking", "wallet"],
        handler: function (response: Record<string, unknown>) {
          resolveOnce(response);
        },
        modal: {
          ondismiss: function () {
            rejectOnce(new Error("Payment popup closed by user."));
          },
        },
        prefill: opts.prefill || {},
        notes: opts.notes || {},
        theme: { color: "#dc3545" },
      };

      // Expose the Razorpay constructor created by the loaded script
      const Razorpay = ((window as unknown) as { Razorpay: RazorpayConstructor }).Razorpay;
      const rzp = new Razorpay(options);
      rzp.on?.("payment.failed", () => {
        rejectOnce(new Error("Payment failed."));
      });
      rzp.open();
    } catch (err) {
      rejectOnce(err instanceof Error ? err : new Error("Payment failed."));
    }
  });
};

export default { openRazorpayCheckout };
